import Phaser from 'phaser';
import { GAME_CONFIG, PLAYER_CONFIG } from '../../config/game';
import { Player } from '../../entities/Player';
import { MobileControls } from '../../ui/MobileControls';
import { GameStateManager } from '../../managers/GameStateManager';

// Edge physics thresholds for Sobel-based platforms
const EDGE_THRESHOLDS = {
  STAND: 180,      // >180: can stand fully
  SLOW_FALL: 100,  // 100-180: slow fall (friction)
  BOOST: 50,       // 50-100: jump boost only
};

interface EdgeData {
  width: number;
  height: number;
  scale: number;
  edges: number[][];
}

export interface BossSceneConfig {
  levelId: string;
  levelName: string;
  backgroundKey: string;
  bossName: string;
  useEdgePhysics?: boolean; // Enable Sobel edge-based physics
}

/**
 * BaseBossScene - Template for boss battle levels
 * 
 * Provides:
 * - Player with controls
 * - Platforms
 * - Projectiles group
 * - Health UI
 * - Boss health bar
 * - Victory/defeat handling
 * - Level completion callback
 */
export abstract class BaseBossScene extends Phaser.Scene {
  protected player!: Player;
  protected platforms!: Phaser.Physics.Arcade.StaticGroup;
  protected projectiles!: Phaser.Physics.Arcade.Group;
  protected hearts: Phaser.GameObjects.Image[] = [];
  protected bossHealthBar!: Phaser.GameObjects.Graphics;
  protected bossHealthBg!: Phaser.GameObjects.Graphics;
  protected isPaused: boolean = false;
  protected isGameOver: boolean = false;
  protected cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  protected jumpKey!: Phaser.Input.Keyboard.Key;
  protected dashKey!: Phaser.Input.Keyboard.Key;
  protected attackKey!: Phaser.Input.Keyboard.Key;
  protected mobileControls?: MobileControls;
  
  protected config: BossSceneConfig;
  
  // Edge physics properties (optional)
  protected edgeData?: EdgeData;
  protected edgeOverlay?: Phaser.GameObjects.Image;
  protected currentJumpBoost: number = 1.0;
  
  // Tutorial overlay
  protected tutorialShown: boolean = false;

  constructor(sceneKey: string, config: BossSceneConfig) {
    super(sceneKey);
    this.config = config;
  }

  /**
   * Helper to check if scene is still active. Use in all async callbacks
   * to prevent crashes when scene is destroyed/restarted.
   */
  protected isSceneActive(): boolean {
    return this.scene && this.scene.isActive(this.scene.key);
  }

  /**
   * Safe delayed call that only executes if scene is still active
   */
  protected safeDelayedCall(delay: number, callback: () => void): Phaser.Time.TimerEvent {
    return this.time.delayedCall(delay, () => {
      if (this.isSceneActive()) {
        callback();
      }
    });
  }

  create() {
    const { width, height } = GAME_CONFIG;
    this.isGameOver = false;
    this.isPaused = false;

    // Background
    this.createBackground(width, height);

    // Create platforms
    this.createPlatforms(width, height);

    // Create projectiles group
    this.projectiles = this.physics.add.group({
      classType: Phaser.Physics.Arcade.Image,
      runChildUpdate: true,
    });

    // Create player
    this.player = new Player(this, width * 0.15, height - 150);

    // Collisions
    this.physics.add.collider(this.player, this.platforms);

    // Player hit by projectiles
    this.physics.add.overlap(
      this.player,
      this.projectiles,
      this.handleProjectileHit as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this
    );

    // Setup controls
    this.setupControls();

    // UI
    this.createUI(width);

    // Create the boss (implemented by subclass)
    this.createBoss(width, height);

    // Boss intro
    this.showBossIntro(width, height);

    // Fade in
    this.cameras.main.fadeIn(500, 0, 0, 0);
  }

  protected createBackground(width: number, height: number) {
    // Try to use generated background
    if (this.textures.exists(this.config.backgroundKey)) {
      const bgImage = this.add.image(width / 2, height / 2, this.config.backgroundKey);
      bgImage.setDisplaySize(width, height);
      bgImage.setDepth(0);
    } else {
      // Fallback gradient
      const bg = this.add.graphics();
      bg.fillGradientStyle(0x2d1b4e, 0x2d1b4e, 0x1a1a2e, 0x1a1a2e, 1);
      bg.fillRect(0, 0, width, height);
    }

    // Floor for gameplay
    const floor = this.add.graphics();
    floor.fillStyle(0x5d4037, 1);
    floor.fillRect(0, height - 80, width, 80);
    floor.setDepth(1);
  }

  // ============ EDGE PHYSICS METHODS (Optional) ============

  /**
   * Load Sobel edge data from JSON cache for edge-based physics
   */
  protected loadEdgeData(): void {
    if (!this.config.useEdgePhysics) return;
    
    const edgeKey = `edges_${this.config.levelId}`;
    try {
      this.edgeData = this.cache.json.get(edgeKey) as EdgeData;
      if (this.edgeData) {
        console.log(`[${this.config.levelName}] Loaded edge data: ${this.edgeData.width}x${this.edgeData.height}, scale ${this.edgeData.scale}`);
      }
    } catch (e) {
      console.warn(`[${this.config.levelName}] Could not load edge data:`, e);
    }
  }

  /**
   * Create gold-tinted edge overlay for visual feedback
   */
  protected createEdgeOverlay(width: number, height: number): void {
    if (!this.config.useEdgePhysics) return;
    
    const frictionKey = `friction_${this.config.levelId}`;
    if (this.textures.exists(frictionKey)) {
      this.edgeOverlay = this.add.image(width / 2, height / 2, frictionKey);
      this.edgeOverlay.setDisplaySize(width, height);
      this.edgeOverlay.setTint(0xffd700); // Gold tint
      this.edgeOverlay.setBlendMode(Phaser.BlendModes.ADD);
      this.edgeOverlay.setAlpha(0.4);
      this.edgeOverlay.setDepth(3);
      
      // Subtle shimmer animation
      this.tweens.add({
        targets: this.edgeOverlay,
        alpha: { from: 0.35, to: 0.45 },
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /**
   * Get edge strength at a specific pixel position
   */
  protected getEdgeStrength(x: number, y: number): number {
    if (!this.edgeData) return 0;
    
    const scale = this.edgeData.scale || 2;
    const ix = Math.floor(x / scale);
    const iy = Math.floor(y / scale);
    
    if (iy < 0 || iy >= this.edgeData.edges.length) return 0;
    if (ix < 0 || ix >= (this.edgeData.edges[iy]?.length || 0)) return 0;
    
    return this.edgeData.edges[iy][ix] || 0;
  }

  /**
   * Apply edge-based physics to player (call in update if using edge physics)
   */
  protected applyEdgePhysics(): void {
    // Guard against invalid scene state
    if (!this.isSceneActive() || this.isGameOver || this.isPaused) return;
    if (!this.config.useEdgePhysics || !this.edgeData || !this.player) return;
    if (!this.player.active || !this.player.body) return;
    
    const playerBody = this.player.body as Phaser.Physics.Arcade.Body;
    
    // If player is on real ground, don't mess with edge physics
    const onRealGround = playerBody.blocked.down || playerBody.touching.down;
    if (onRealGround) {
      this.player.setExternalGround(false);
      this.currentJumpBoost = 1.0;
      return;
    }
    
    const playerHeight = playerBody.height;
    
    // Sample edge strength at player's feet
    const feetX = this.player.x;
    const feetY = this.player.y + playerHeight / 2;
    const edgeStrength = this.getEdgeStrength(feetX, feetY);
    
    // Sample left and right for wider detection
    const leftStrength = this.getEdgeStrength(feetX - 10, feetY);
    const rightStrength = this.getEdgeStrength(feetX + 10, feetY);
    const maxStrength = Math.max(edgeStrength, leftStrength, rightStrength);
    
    // Apply physics based on edge strength
    if (maxStrength > EDGE_THRESHOLDS.STAND) {
      // Strong edge: full support (only when falling or standing still)
      if (playerBody.velocity.y > 0) {
        playerBody.setVelocityY(0);
        this.player.setExternalGround(true);
      } else if (playerBody.velocity.y === 0) {
        this.player.setExternalGround(true);
      }
      this.currentJumpBoost = 1.0 + (maxStrength - EDGE_THRESHOLDS.STAND) / 200;
    } else if (maxStrength > EDGE_THRESHOLDS.SLOW_FALL) {
      // Medium edge: friction/slow fall
      if (playerBody.velocity.y > 0) {
        playerBody.setVelocityY(playerBody.velocity.y * 0.85);
      }
      this.currentJumpBoost = 1.2;
      this.player.setExternalGround(false);
    } else if (maxStrength > EDGE_THRESHOLDS.BOOST) {
      // Weak edge: jump boost only
      this.currentJumpBoost = 1.15;
      this.player.setExternalGround(false);
    } else {
      // No edge support
      this.currentJumpBoost = 1.0;
      this.player.setExternalGround(false);
    }
  }

  protected createPlatforms(width: number, height: number) {
    this.platforms = this.physics.add.staticGroup();

    // Main ground
    const ground = this.add.rectangle(width / 2, height - 40, width, 80, 0x5d4037);
    this.physics.add.existing(ground, true);
    this.platforms.add(ground);

    // Platform left
    const platLeft = this.add.rectangle(100, height - 200, 120, 20, 0x4a5568);
    this.physics.add.existing(platLeft, true);
    this.platforms.add(platLeft);

    // Platform right
    const platRight = this.add.rectangle(width - 100, height - 200, 120, 20, 0x4a5568);
    this.physics.add.existing(platRight, true);
    this.platforms.add(platRight);

    // Platform center high
    const platCenter = this.add.rectangle(width / 2, height - 350, 150, 20, 0x4a5568);
    this.physics.add.existing(platCenter, true);
    this.platforms.add(platCenter);
  }

  protected setupControls() {
    if (this.input.keyboard) {
      this.cursors = this.input.keyboard.createCursorKeys();
      this.jumpKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
      this.dashKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT);
      this.attackKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.X);

      // Pause
      this.input.keyboard.on('keydown-ESC', () => this.togglePause());
      this.input.keyboard.on('keydown-P', () => this.togglePause());
    }

    // Mobile controls
    if (MobileControls.shouldShow()) {
      this.mobileControls = new MobileControls(this);
      
      this.events.on('mobile-jump-down', () => {
        if (!this.isPaused && !this.isGameOver && this.player.isOnGround()) {
          if (this.config.useEdgePhysics) {
            this.player.jumpWithBoost(this.currentJumpBoost);
            this.currentJumpBoost = 1.0;
          } else {
            this.player.jump();
          }
        }
      });
      
      this.events.on('mobile-dash-down', () => {
        if (!this.isPaused && !this.isGameOver) {
          this.player.dash(this.player.flipX ? -1 : 1);
        }
      });

      this.events.on('mobile-attack-down', () => {
        if (!this.isPaused && !this.isGameOver) {
          this.handlePlayerAttack();
        }
      });
    }
  }

  protected createUI(width: number) {
    // Hearts
    for (let i = 0; i < PLAYER_CONFIG.maxHealth; i++) {
      const heart = this.add.image(30 + i * 30, 30, 'heart');
      heart.setDepth(100);
      this.hearts.push(heart);
    }

    // Boss health bar background
    this.bossHealthBg = this.add.graphics();
    this.bossHealthBg.fillStyle(0x333333, 1);
    this.bossHealthBg.fillRect(width / 2 - 100, 60, 200, 16);
    this.bossHealthBg.setDepth(100);

    // Boss health bar
    this.bossHealthBar = this.add.graphics();
    this.bossHealthBar.setDepth(100);
    this.updateBossHealthBar(1);

    // Boss name
    this.add.text(width / 2, 45, this.config.bossName, {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#ffffff',
    }).setOrigin(0.5).setDepth(100);
  }

  protected updateBossHealthBar(percentage: number) {
    const { width } = GAME_CONFIG;
    this.bossHealthBar.clear();
    this.bossHealthBar.fillStyle(0xff4444, 1);
    this.bossHealthBar.fillRect(width / 2 - 98, 62, 196 * Math.max(0, percentage), 12);
  }

  protected showBossIntro(width: number, height: number) {
    // Level name
    const levelText = this.add.text(width / 2, height / 2 - 40, this.config.levelName, {
      fontFamily: 'Georgia, serif',
      fontSize: '32px',
      color: '#ffd700',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(200);

    // Boss subtitle
    const bossText = this.add.text(width / 2, height / 2 + 10, `VS ${this.config.bossName}`, {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: '#ff6b6b',
    }).setOrigin(0.5).setDepth(200);

    // Fade out intro text
    this.tweens.add({
      targets: [levelText, bossText],
      alpha: 0,
      delay: 1500,
      duration: 500,
      onComplete: () => {
        if (!this.isSceneActive()) return;
        if (levelText && levelText.scene) levelText.destroy();
        if (bossText && bossText.scene) bossText.destroy();
        
        // Show tutorial if first time playing this level type
        this.showTutorialIfNeeded();
      },
    });
  }
  
  /**
   * Show tutorial overlay if player hasn't seen it for this level type
   */
  protected showTutorialIfNeeded() {
    // Check localStorage for tutorial flag
    const tutorialKey = `tutorial_boss_seen`;
    if (typeof localStorage !== 'undefined' && localStorage.getItem(tutorialKey)) {
      return; // Already seen
    }
    
    const { width, height } = GAME_CONFIG;
    
    // Create tutorial overlay
    const overlay = this.add.container(width / 2, height / 2);
    overlay.setDepth(250);
    
    const bg = this.add.rectangle(0, 0, 320, 200, 0x000000, 0.85);
    
    const title = this.add.text(0, -70, '🎮 CONTROLS', {
      fontFamily: 'Georgia, serif',
      fontSize: '20px',
      color: '#ffd700',
    }).setOrigin(0.5);
    
    const controls = this.add.text(0, 0, 
      '← → or Joystick: Move\n' +
      '↑ or Jump Button: Jump\n' +
      'X or Attack Button: Attack\n' +
      'SHIFT or Dash Button: Dash\n\n' +
      '💡 Attack boss when it\'s glowing green!',
    {
      fontFamily: 'Georgia, serif',
      fontSize: '12px',
      color: '#ffffff',
      align: 'center',
      lineSpacing: 4,
    }).setOrigin(0.5);
    
    const tapText = this.add.text(0, 75, 'Tap anywhere to start', {
      fontFamily: 'Georgia, serif',
      fontSize: '11px',
      color: '#888888',
    }).setOrigin(0.5);
    
    overlay.add([bg, title, controls, tapText]);
    
    // Pause game while tutorial is shown
    this.isPaused = true;
    this.physics.pause();
    
    // Tap anywhere to dismiss
    bg.setInteractive();
    bg.on('pointerdown', () => {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(tutorialKey, 'true');
      }
      overlay.destroy();
      this.isPaused = false;
      this.physics.resume();
    });
    
    // Also dismiss with any key
    if (this.input.keyboard) {
      const dismissHandler = () => {
        if (overlay && overlay.scene) {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(tutorialKey, 'true');
          }
          overlay.destroy();
          this.isPaused = false;
          this.physics.resume();
          this.input.keyboard?.off('keydown', dismissHandler);
        }
      };
      this.input.keyboard.on('keydown', dismissHandler);
    }
  }

  protected handleProjectileHit(
    player: Phaser.GameObjects.GameObject,
    projectile: Phaser.GameObjects.GameObject
  ) {
    if (this.isGameOver || this.isPaused || !this.isSceneActive()) return;

    const proj = projectile as Phaser.Physics.Arcade.Image;
    const playerObj = player as Player;

    // Safety check - make sure projectile is still active
    if (!proj || !proj.active) return;

    // Check for parry
    if (playerObj.isParrying && playerObj.isParrying()) {
      proj.destroy();
      this.onParry();
      return;
    }

    // Skip if player is invincible
    if (playerObj.isInvincible && playerObj.isInvincible()) return;

    // Take damage
    proj.destroy();
    const alive = playerObj.takeDamage();
    this.updateHearts();

    if (!alive) {
      this.onDefeat();
    }
  }

  protected updateHearts() {
    const currentHealth = this.player.getHealth();
    this.hearts.forEach((heart, i) => {
      const wasFull = heart.texture.key === 'heart';
      const nowFull = i < currentHealth;
      
      heart.setTexture(nowFull ? 'heart' : 'heart_empty');
      
      // Shake hearts when losing health
      if (wasFull && !nowFull) {
        this.shakeHearts();
      }
    });
  }
  
  protected shakeHearts() {
    // Shake all remaining hearts
    this.hearts.forEach((heart) => {
      if (heart.texture.key === 'heart') {
        this.tweens.add({
          targets: heart,
          x: heart.x + 3,
          duration: 50,
          yoyo: true,
          repeat: 3,
          ease: 'Sine.easeInOut',
        });
      }
    });
    
    // Flash screen red
    this.cameras.main.flash(100, 255, 50, 50);
  }

  protected togglePause() {
    this.isPaused = !this.isPaused;
    
    if (this.isPaused) {
      this.physics.pause();
      this.showPauseMenu();
    } else {
      this.physics.resume();
      this.hidePauseMenu();
    }
  }

  protected pauseMenu?: Phaser.GameObjects.Container;

  protected showPauseMenu() {
    const { width, height } = GAME_CONFIG;
    
    this.pauseMenu = this.add.container(width / 2, height / 2);
    this.pauseMenu.setDepth(300);

    const bg = this.add.rectangle(0, 0, 280, 280, 0x000000, 0.9);
    const title = this.add.text(0, -110, 'PAUSED', {
      fontFamily: 'Georgia, serif',
      fontSize: '28px',
      color: '#ffffff',
    }).setOrigin(0.5);
    
    // Controls reference
    const controlsTitle = this.add.text(0, -75, '🎮 Controls', {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#ffd700',
    }).setOrigin(0.5);
    
    const controlsText = this.add.text(0, -30, 
      '← → Move | ↑ Jump | X Attack\n' +
      'SHIFT Dash | ESC/P Pause',
    {
      fontFamily: 'Georgia, serif',
      fontSize: '11px',
      color: '#aaaaaa',
      align: 'center',
    }).setOrigin(0.5);

    const resumeBtn = this.add.text(0, 35, '▶ Resume', {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: '#88ff88',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    resumeBtn.on('pointerdown', () => this.togglePause());

    const quitBtn = this.add.text(0, 80, '✕ Quit to Map', {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: '#ff8888',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    quitBtn.on('pointerdown', () => {
      this.scene.start('WorldMapScene');
    });

    this.pauseMenu.add([bg, title, controlsTitle, controlsText, resumeBtn, quitBtn]);
  }

  protected hidePauseMenu() {
    this.pauseMenu?.destroy();
    this.pauseMenu = undefined;
  }

  protected onVictory() {
    // Guard against multiple calls
    if (this.isGameOver || !this.isSceneActive()) return;
    
    this.isGameOver = true;
    this.physics.pause();

    const { width, height } = GAME_CONFIG;

    // Calculate stars based on health remaining
    const healthPercent = this.player.getHealth() / PLAYER_CONFIG.maxHealth;
    const stars = healthPercent >= 0.8 ? 3 : healthPercent >= 0.5 ? 2 : 1;

    // Save progress
    GameStateManager.completeLevel(this.config.levelId, { stars });
    
    // Get next level info
    const nextLevel = GameStateManager.getNextLevel(this.config.levelId);
    const nextLevelName = nextLevel ? GameStateManager.getLevelName(nextLevel) : null;

    // Victory screen
    const container = this.add.container(width / 2, height / 2).setDepth(300);

    const bg = this.add.rectangle(0, 0, 320, nextLevel ? 320 : 260, 0x000000, 0.9);
    const title = this.add.text(0, -110, '🎉 VICTORY!', {
      fontFamily: 'Georgia, serif',
      fontSize: '32px',
      color: '#ffd700',
    }).setOrigin(0.5);

    const levelComplete = this.add.text(0, -70, `${this.config.levelName} Complete!`, {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#a78bfa',
    }).setOrigin(0.5);

    const starText = this.add.text(0, -25, '⭐'.repeat(stars) + '☆'.repeat(3 - stars), {
      fontSize: '32px',
    }).setOrigin(0.5);

    container.add([bg, title, levelComplete, starText]);

    // Next level unlocked message
    if (nextLevelName) {
      const unlockedText = this.add.text(0, 20, `🔓 UNLOCKED: ${nextLevelName}`, {
        fontFamily: 'Georgia, serif',
        fontSize: '12px',
        color: '#22c55e',
      }).setOrigin(0.5);
      container.add(unlockedText);
    }

    // Map button
    const mapBtn = this.add.text(0, nextLevel ? 65 : 50, '🗺️ World Map', {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: '#ffd700',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    
    mapBtn.on('pointerover', () => mapBtn.setScale(1.1));
    mapBtn.on('pointerout', () => mapBtn.setScale(1));
    mapBtn.on('pointerdown', () => {
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.safeDelayedCall(300, () => {
        this.scene.start('WorldMapScene');
      });
    });
    container.add(mapBtn);

    // Next level button
    if (nextLevel) {
      const nextBtn = this.add.text(0, 105, '▶ Next Level', {
        fontFamily: 'Georgia, serif',
        fontSize: '18px',
        color: '#22c55e',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      
      nextBtn.on('pointerover', () => nextBtn.setScale(1.1));
      nextBtn.on('pointerout', () => nextBtn.setScale(1));
      nextBtn.on('pointerdown', () => {
        this.cameras.main.fadeOut(300, 0, 0, 0);
        this.safeDelayedCall(300, () => {
          const nextSceneKey = GameStateManager.getLevelSceneKey(nextLevel);
          this.scene.start(nextSceneKey);
        });
      });
      container.add(nextBtn);
    }
  }

  protected onDefeat() {
    // Guard against multiple calls
    if (this.isGameOver || !this.isSceneActive()) return;
    
    this.isGameOver = true;
    this.physics.pause();

    const { width, height } = GAME_CONFIG;

    const container = this.add.container(width / 2, height / 2).setDepth(300);

    const bg = this.add.rectangle(0, 0, 280, 200, 0x000000, 0.9);
    const title = this.add.text(0, -60, '💔 DEFEATED', {
      fontFamily: 'Georgia, serif',
      fontSize: '28px',
      color: '#ff6666',
    }).setOrigin(0.5);

    const retryBtn = this.add.text(0, 10, '↻ Try Again', {
      fontFamily: 'Georgia, serif',
      fontSize: '20px',
      color: '#88ff88',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    retryBtn.on('pointerdown', () => this.scene.restart());

    const quitBtn = this.add.text(0, 60, '← Back to Map', {
      fontFamily: 'Georgia, serif',
      fontSize: '16px',
      color: '#aaaaaa',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    quitBtn.on('pointerdown', () => {
      this.scene.start('WorldMapScene');
    });

    container.add([bg, title, retryBtn, quitBtn]);
  }

  // Abstract methods to be implemented by subclasses
  protected abstract createBoss(width: number, height: number): void;
  protected abstract handlePlayerAttack(): void;
  protected abstract onParry(): void;

  update(time: number, delta: number) {
    if (this.isPaused || this.isGameOver) return;

    // Handle player movement
    let moveX = 0;
    
    // Check mobile controls first
    if (this.mobileControls) {
      const joystick = this.mobileControls.getMovementVector();
      if (joystick.x !== 0) {
        moveX = joystick.x;
      }
    }
    
    // Fall back to keyboard
    if (moveX === 0 && this.cursors) {
      if (this.cursors.left.isDown) moveX = -1;
      else if (this.cursors.right.isDown) moveX = 1;
    }

    this.player.move(moveX);

    // Jump (with edge physics boost if enabled)
    if (this.jumpKey?.isDown || this.cursors?.up.isDown) {
      if (this.player.isOnGround()) {
        if (this.config.useEdgePhysics) {
          this.player.jumpWithBoost(this.currentJumpBoost);
          this.currentJumpBoost = 1.0; // Reset after jump
        } else {
          this.player.jump();
        }
      }
    }

    // Dash
    if (this.dashKey?.isDown) {
      this.player.dash(moveX || (this.player.flipX ? -1 : 1));
    }

    // Attack
    if (this.attackKey?.isDown) {
      this.handlePlayerAttack();
    }

    // Update mobile controls
    this.mobileControls?.update();
    
    // Apply edge physics if enabled
    if (this.config.useEdgePhysics) {
      this.applyEdgePhysics();
    }

    // Update boss (implemented by subclass)
    this.updateBoss(time, delta);
  }

  protected abstract updateBoss(time: number, delta: number): void;
  
  /**
   * Clean up when scene is destroyed or restarted.
   * Override in subclasses to add specific cleanup.
   */
  shutdown() {
    // Clear any remaining tweens
    this.tweens.killAll();
    
    // Clear pause menu
    if (this.pauseMenu) {
      this.pauseMenu.destroy();
      this.pauseMenu = undefined;
    }
    
    // Clear mobile controls
    this.mobileControls = undefined;
    
    // Reset state
    this.isGameOver = false;
    this.isPaused = false;
  }
}
